import { useEffect, useMemo, useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import Chip from "@mui/material/Chip";

import debounce from "lodash/debounce";

import { AUTOCOMPLETE_PAGE_SIZE, TagTypes } from "constants/tags";
import { useGetTagsCountQuery, useGetTagsQuery } from "services/tags";
import { AssignedTag } from "services/types/assigned-tag.type";
import { Tag } from "services/types/tags.type";

import PatientTagsAutocomplete from "./tags-autocomplete.component";

const SEARCH_DEBOUNCE_MS = 250;

type AddAssignedTagProps = {
  existingTags: AssignedTag[];
  onAdd: (tag_id: number, tag: Tag) => void;
  tagType?: TagTypes;
  filterActiveOnly?: boolean;
};

export default function AddAssignedTag({
  tagType,
  existingTags,
  onAdd,
  filterActiveOnly = false
}: AddAssignedTagProps) {
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const debouncedSetSearch = useMemo(
    () => debounce(setDebouncedSearch, SEARCH_DEBOUNCE_MS),
    []
  );
  useEffect(() => {
    debouncedSetSearch(searchInput);
  }, [searchInput, debouncedSetSearch]);
  useEffect(() => () => debouncedSetSearch.cancel(), [debouncedSetSearch]);

  const countFilterArgs = {
    ...(tagType ? { type: tagType } : {}),
    ...(filterActiveOnly ? { is_active: true } : {}),
    search: debouncedSearch || undefined
  };
  const {
    data: tags = [],
    isFetching,
    isLoading,
    isUninitialized
  } = useGetTagsQuery({
    ...countFilterArgs,
    limit: AUTOCOMPLETE_PAGE_SIZE
  });
  const { data: totalCount = 0 } = useGetTagsCountQuery(countFilterArgs);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<any>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const existingTagIds = new Set<number>(
    existingTags.filter(Boolean).map((tag) => tag.tag.id)
  );

  // Show "type to search for more" only when the server has more matches
  // than what fits in one page. Comparing against the real totalCount avoids
  // the false positive at exactly AUTOCOMPLETE_PAGE_SIZE tags.
  const isAtCap = totalCount > tags.length;

  const addableCount = tags.reduce(
    (count, tag) => (existingTagIds.has(tag.id) ? count : count + 1),
    0
  );

  // Hide the "add tag" chip ONLY in the idle/closed/unfiltered state: server
  // confirms last page (`tags.length < page size`), no addable tags, popover
  // is closed, and there's no active search. Unmounting mid-search would yank
  // the popover out from under the user — if they're typing a search whose
  // results happen to all be already-assigned, the chip + popover would
  // disappear and they'd have no way to clear the search. Same problem with
  // server pagination: a 50-row page might be entirely already-assigned but
  // more tags exist beyond page 1.
  const isLastPage = tags.length < AUTOCOMPLETE_PAGE_SIZE;
  const isPopoverOpen = Boolean(anchorEl);
  const isSearching = searchInput !== "";
  if (
    !addableCount &&
    isLastPage &&
    !isPopoverOpen &&
    !isSearching &&
    !(isFetching || isLoading || isUninitialized)
  )
    return null;
  return (
    <>
      <Chip
        size="small"
        variant="outlined"
        label="add tag"
        icon={<AddIcon />}
        onClick={handleClick}
        sx={{
          mb: 0.75,
          mr: 1,
          opacity: 0.8,
          cursor: "pointer",
          "&:hover": {
            opacity: 1
          }
        }}
      />

      <PatientTagsAutocomplete
        loading={isLoading || isUninitialized}
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        options={tags}
        existingTagIds={existingTagIds}
        onClose={handleClose}
        onSelect={(tag) => tag && onAdd(Number(tag.id), tag)}
        onSearchChange={setSearchInput}
        isAtCap={isAtCap}
      />
    </>
  );
}
